"use client";

import { Amplify } from "aws-amplify";
import { buildAmplifyConfig, type CognitoConfig } from "@/lib/amplify-config";

/**
 * Tracks which Cognito pool is currently configured in the Amplify singleton.
 *
 * We track the POOL (not just a boolean) so that:
 *   - ConfigureAmplify (customer) skips re-configuring if the employee pool
 *     is already active, preventing the customer pool from reasserting itself
 *     on any root-layout re-render while on /employee/* routes.
 *   - When the user navigates back to the main app, ConfigureAmplify sees
 *     "employee" (not "customer") and correctly re-applies the customer pool.
 */
type ConfiguredPool = "customer" | "employee" | "none";
let _configuredPool: ConfiguredPool = "none";

/** Called by EmployeeConfigureAmplify to mark the employee pool as active. */
export function markEmployeePoolConfigured(): void {
  _configuredPool = "employee";
}

/**
 * Called by EmployeeConfigureAmplify's useEffect cleanup when navigating away
 * from /employee/* routes so ConfigureAmplify re-applies the customer pool.
 */
export function resetConfiguredPool(): void {
  _configuredPool = "none";
}

/**
 * Configures the Amplify client with the customer Cognito pool resolved
 * server-side by the root layout. Runs synchronously on every render but
 * skips if the employee pool is already active (EmployeeConfigureAmplify
 * took over for an /employee/* route).
 *
 * The typeof window guard skips execution during SSR; the server-side auth
 * path (lib/amplify-server.ts / createServerRunner) is independent.
 */
export default function ConfigureAmplify({ config }: { config: CognitoConfig }) {
  // Skip if the employee pool is actively configured (EmployeeConfigureAmplify
  // took over). The check is "!== employee" (not "!== customer") so that
  // ConfigureAmplify never overwrites an active employee session — even on
  // re-renders — while still re-applying the customer pool when the employee
  // pool is not active (pool is "none" or "customer").
  if (typeof window !== "undefined" && _configuredPool !== "employee") {
    Amplify.configure(buildAmplifyConfig(config), { ssr: true });
    _configuredPool = "customer";
  }

  return null;
}
